import React, { useCallback, useEffect, useState } from "react";
import ClientLayout from "../../components/layouts/ClientLayout";
import { auth, database, firestore } from "../../firebaseConfig";
import { ref, onValue } from "firebase/database";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import toast from "react-hot-toast";
import { DollarSign, CreditCard, Calendar, Wallet, Building2, Phone, X } from "lucide-react";
import { API_BASE_URL } from "../../utils/apiBase";
import ChatModal from "../../components/messaging/ChatModal";

/**
 * ClientPayments Component
 *
 * Displays all payments (and recent purchases) made by the client across all companies they're connected to.
 * Payments are fetched from Firebase Realtime Database at:
 * - App payments: Companies/{companyName}/ClientPayments/{clientUsername}/payments/{paymentId}
 * - M-Pesa payments: Companies/{companyName}/CustomerRecords/{clientUsername}/Payments/{paymentId}
 * Purchases are fetched from:
 * - Companies/{companyName}/ClientPurchases/{clientUsername}/purchases/{purchaseId}
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Helper function to get payment date
const getPaymentDate = (payment) => {
  return payment.paymentDate || payment.TransactionDate || payment.date || payment.Date;
};

const PAGE_SIZE = 10; // pagination size for the full payments table

const ClientPayments = () => {
  const [payments, setPayments] = useState([]); // app payments
  const [mpesaPayments, setMpesaPayments] = useState([]); // M-Pesa payments from CustomerRecords
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [connectedCompanies, setConnectedCompanies] = useState([]);
  const [companyClientMap, setCompanyClientMap] = useState({});
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [companyBalances, setCompanyBalances] = useState({});
  const [companyPaybills, setCompanyPaybills] = useState({});
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [user, setUser] = useState(null);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [chatModalCompany, setChatModalCompany] = useState(null);
  const [chatModalParticipant, setChatModalParticipant] = useState(null);

  // New: companyFilter controls which company's records are shown by default.
  // If there's exactly one connected company we auto-select it; otherwise user chooses.
  const [companyFilter, setCompanyFilter] = useState("all");

  // Pagination state for the full payments list
  const [currentPage, setCurrentPage] = useState(1);

  // Recent purchases aggregated across companies (used for the purchases table)
  const [recentPurchases, setRecentPurchases] = useState([]);

  // Robust helper to determine client's username/key for a given company.
  // Accepts values stored under legacy `client`, current `clientSnapshots`, or other obvious fields.
  const getClientUsername = useCallback(
    (companyName) => {
      // 1) prefer explicit mapping (may come from data.client or data.clientSnapshots)
      if (companyClientMap && companyClientMap[companyName]) {
        return companyClientMap[companyName];
      }

      // 2) check clientSnapshots directly in clientData (defensive)
      if (clientData?.clientSnapshots && clientData.clientSnapshots[companyName]) {
        return clientData.clientSnapshots[companyName];
      }

      // 3) try legacy mapping fields
      if (clientData?.client && clientData.client[companyName]) {
        return clientData.client[companyName];
      }

      // 4) fallback fields: OtherName / otherName / username
      return (
        clientData?.OtherName ||
        clientData?.otherName ||
        clientData?.username ||
        user?.uid
      );
    },
    [companyClientMap, clientData, user?.uid]
  );

  const updateCompanyPaybill = useCallback((companyName, paybillNumber) => {
    const normalizedPaybill = paybillNumber ? String(paybillNumber).trim() : "";
    if (!normalizedPaybill) return;
    setCompanyPaybills((prev) => {
      if (prev[companyName] === normalizedPaybill) return prev;
      return {
        ...prev,
        [companyName]: normalizedPaybill,
      };
    });
  }, []);

  const openChatModal = useCallback(
    (companyName) => {
      if (!companyName) return;
      setChatModalCompany(companyName);
      setChatModalParticipant(getClientUsername(companyName));
      setIsChatModalOpen(true);
    },
    [getClientUsername]
  );

  // Set up auth listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Load client profile and connected companies; merge mapping fields
  useEffect(() => {
    if (!user) return;

    const loadClientData = async () => {
      try {
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (!userDoc.exists()) return;

        const data = userDoc.data();
        setClientData(data);

        // Get list of companies the client is connected to
        const companies = data.connectedCompanies || [];
        setConnectedCompanies(companies);

        // Merge mapping sources: legacy `client`, new `clientSnapshots`, etc.
        const mergedMap = {
          ...(data.client || {}),
          ...(data.clientSnapshots || {}),
        };

        if (Object.keys(mergedMap).length > 0) {
          setCompanyClientMap(mergedMap);
        }
      } catch (error) {
        console.error("Error loading client data:", error);
        toast.error("Failed to load profile");
      }
    };

    loadClientData();
  }, [user]);

  // Keep companyFilter in sync with connectedCompanies:
  // - If exactly 1 company, auto-select it.
  // - If multiple and current filter is invalid, default to first company.
  useEffect(() => {
    if (!connectedCompanies || connectedCompanies.length === 0) {
      setCompanyFilter("all");
      return;
    }
    if (connectedCompanies.length === 1) {
      setCompanyFilter(connectedCompanies[0]);
      return;
    }
    // multiple companies
    setCompanyFilter((current) => {
      if (current && current !== "all" && connectedCompanies.includes(current)) {
        return current; // keep existing valid selection
      }
      // otherwise default to first company in the list
      return connectedCompanies[0];
    });
  }, [connectedCompanies]);

  // Reset pagination when filters or data change
  useEffect(() => {
    setCurrentPage(1);
  }, [companyFilter, selectedMonth, selectedYear, searchTerm, payments, mpesaPayments]);

  // Load company balances and paybills
  useEffect(() => {
    if (!clientData || connectedCompanies.length === 0) return;

    const loadCompanyData = () => {
      const balances = {};
      const unsubscribers = [];

      connectedCompanies.forEach((companyName) => {
        const clientUsername = getClientUsername(companyName);

        // RTDB reference for ValidatedClients using the derived key (snapshot id or username)
        const clientRef = ref(
          database,
          `Companies/${companyName}/ValidatedClients/${clientUsername}`
        );

        const unsubscribe = onValue(clientRef, async (snapshot) => {
          if (snapshot.exists()) {
            const clientInfo = snapshot.val();
            balances[companyName] = {
              balance: clientInfo.Balance || 0,
              unitPrice: clientInfo.UnitPrice || 0,
            };

            // Prefer PaybillNumber (canonical ValidatedClients schema); fall back to paybillNumber for legacy records prior to migration
            const paybillNumber = clientInfo.PaybillNumber || clientInfo.paybillNumber;
            if (paybillNumber) {
              updateCompanyPaybill(companyName, paybillNumber);
            } else {
              // If paybill not present in RTDB, try to derive otherName from RTDB snapshot and query Firestore
              const otherNameFromSnapshot = clientInfo.OtherName || clientInfo.otherName;
              if (otherNameFromSnapshot) {
                try {
                  const paybillQuery = query(
                    collection(firestore, "paybills"),
                    where("companyName", "==", companyName),
                    where("otherName", "==", otherNameFromSnapshot)
                  );
                  const paybillSnap = await getDocs(paybillQuery);
                  if (!paybillSnap.empty) {
                    const paybillData = paybillSnap.docs[0].data();
                    updateCompanyPaybill(companyName, paybillData.paybillNumber);
                  }
                } catch (error) {
                  console.error(`Error loading paybill by snapshot otherName for ${companyName}:`, error);
                }
              }
            }

            // Only update state once all companies are loaded
            if (Object.keys(balances).length === connectedCompanies.length) {
              setCompanyBalances({ ...balances });
            }
          }
        });

        unsubscribers.push(unsubscribe);

        // Also attempt to fetch paybill from Firestore using user-side mapping (clientUsername)
        // This handles the case where clientUsername is actually the OtherName string.
        (async () => {
          try {
            if (clientUsername) {
              const paybillQuery = query(
                collection(firestore, "paybills"),
                where("companyName", "==", companyName),
                where("otherName", "==", clientUsername)
              );
              const paybillSnap = await getDocs(paybillQuery);
              if (!paybillSnap.empty) {
                const paybillData = paybillSnap.docs[0].data();
                updateCompanyPaybill(companyName, paybillData.paybillNumber);
              }
            }
          } catch (error) {
            console.error(`Error loading paybill for ${companyName}:`, error);
          }
        })();
      });

      // return cleanup to unsubscribe RTDB listeners
      return () => {
        unsubscribers.forEach((unsub) => unsub && unsub());
      };
    };

    const cleanup = loadCompanyData();
    return cleanup;
  }, [clientData, connectedCompanies, updateCompanyPaybill, user, getClientUsername]);

  // Discover all available payment years across companies so users can search far back
  useEffect(() => {
    if (!clientData || connectedCompanies.length === 0) return;

    const yearSet = new Set();
    const unsubscribers = [];

    connectedCompanies.forEach((companyName) => {
      const clientUsername = getClientUsername(companyName);
      const paymentsRootRef = ref(
        database,
        `Companies/${companyName}/ClientPayments/${clientUsername}/payments`
      );

      const unsubscribe = onValue(paymentsRootRef, (snapshot) => {
        if (snapshot.exists()) {
          Object.keys(snapshot.val() || {}).forEach((yearKey) => {
            const parsedYear = parseInt(yearKey, 10);
            if (!Number.isNaN(parsedYear)) {
              yearSet.add(parsedYear);
            }
          });
          if (yearSet.size > 0) {
            const sortedYears = Array.from(yearSet).sort((a, b) => b - a);
            setAvailableYears(sortedYears);
          }
        }
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub && unsub());
    };
  }, [clientData, connectedCompanies, user, getClientUsername]);

  // Keep selectedYear in sync when new historical years are discovered
  useEffect(() => {
    if (!availableYears.length) return;
    if (selectedYear !== "all" && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Load payments from all connected companies (supports multi-year search)
  useEffect(() => {
    if (!clientData || connectedCompanies.length === 0) {
      setLoading(false);
      return;
    }

    const paymentsByCompany = {};
    const processedCompanies = new Set();
    let listeners = [];

    const addYearPayments = (yearKey, yearData, companyName, target) => {
      Object.entries(yearData || {}).forEach(([monthOrId, monthData]) => {
        if (typeof monthData === "object" && monthData !== null) {
          if (MONTHS.includes(monthOrId)) {
            Object.entries(monthData).forEach(([id, payment]) => {
              target.push({
                id,
                ...payment,
                companyName,
                month: monthOrId,
                year: parseInt(yearKey, 10),
              });
            });
          } else {
            target.push({
              id: monthOrId,
              ...monthData,
              companyName,
              year: parseInt(yearKey, 10),
            });
          }
        }
      });
    };

    const combineAndSet = () => {
      const combined = Object.values(paymentsByCompany).flat();
      combined.sort((a, b) => {
        const dateA = new Date(getPaymentDate(a));
        const dateB = new Date(getPaymentDate(b));
        return dateB - dateA;
      });
      setPayments(combined);
    };

    setLoading(true);

    connectedCompanies.forEach((companyName) => {
      const clientKey = getClientUsername(companyName);
      const paymentsPath =
        selectedYear === "all"
          ? `Companies/${companyName}/ClientPayments/${clientKey}/payments`
          : `Companies/${companyName}/ClientPayments/${clientKey}/payments/${selectedYear}`;

      const paymentsRef = ref(database, paymentsPath);
      const unsubscribe = onValue(
        paymentsRef,
        (snapshot) => {
          const companyPayments = [];
          if (snapshot.exists()) {
            const paymentsData = snapshot.val();
            if (selectedYear === "all") {
              Object.entries(paymentsData).forEach(([yearKey, yearData]) => {
                addYearPayments(yearKey, yearData, companyName, companyPayments);
              });
            } else {
              addYearPayments(selectedYear, paymentsData, companyName, companyPayments);
            }
          }

          paymentsByCompany[companyName] = companyPayments;
          if (!processedCompanies.has(companyName)) {
            processedCompanies.add(companyName);
            if (processedCompanies.size === connectedCompanies.length) {
              setLoading(false);
            }
          }
          combineAndSet();
        },
        (error) => {
          console.error(`Error loading payments from ${companyName}:`, error);
          if (!processedCompanies.has(companyName)) {
            processedCompanies.add(companyName);
            if (processedCompanies.size === connectedCompanies.length) {
              setLoading(false);
            }
          }
        }
      );
      listeners.push(unsubscribe);
    });

    return () => {
      listeners.forEach((unsubscribe) => unsubscribe && unsubscribe());
    };
  }, [clientData, connectedCompanies, user, selectedYear, getClientUsername]);

  // Load recent purchases across connected companies (side-by-side table)
  useEffect(() => {
    if (!clientData || connectedCompanies.length === 0) {
      setRecentPurchases([]);
      return;
    }

    const purchasesByCompany = {};
    const unsubscribers = [];

    connectedCompanies.forEach((companyName) => {
      const clientKey = getClientUsername(companyName);
      const purchasesPath = `Companies/${companyName}/ClientPurchases/${clientKey}/purchases`;
      const purchasesRef = ref(database, purchasesPath);

      const unsubscribe = onValue(purchasesRef, (snapshot) => {
        const companyPurchases = [];
        if (snapshot.exists()) {
          const data = snapshot.val();
          Object.entries(data || {}).forEach(([yearKey, months]) => {
            Object.entries(months || {}).forEach(([monthKey, items]) => {
              Object.values(items || {}).forEach((purchase) => {
                companyPurchases.push({
                  ...purchase,
                  companyName,
                  year: parseInt(yearKey, 10),
                  month: monthKey,
                });
              });
            });
          });
        }
        purchasesByCompany[companyName] = companyPurchases;

        // Combine, sort by most recent, and keep top N (we show 10)
        const combined = Object.values(purchasesByCompany).flat();
        combined.sort((a, b) => {
          const dateA = new Date(a.purchaseDate || a.date || a.PurchaseDate || 0);
          const dateB = new Date(b.purchaseDate || b.date || b.PurchaseDate || 0);
          return dateB - dateA;
        });
        setRecentPurchases(combined.slice(0, 10));
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((u) => u && u());
    };
  }, [clientData, connectedCompanies, getClientUsername]);

  // Load M-Pesa payments (CustomerRecords) across connected companies (similar to ViewClientModal)
  useEffect(() => {
    if (!clientData || connectedCompanies.length === 0) {
      setMpesaPayments([]);
      return;
    }

    const mpesaByCompany = {};
    const unsubscribers = [];

    connectedCompanies.forEach((companyName) => {
      const clientKey = getClientUsername(companyName);
      const mpesaPath = `Companies/${companyName}/CustomerRecords/${clientKey}/Payments`;
      const mpesaRef = ref(database, mpesaPath);

      const unsubscribe = onValue(mpesaRef, (snapshot) => {
        const companyMpesa = [];
        if (snapshot.exists()) {
          const data = snapshot.val();
          Object.entries(data || {}).forEach(([yearKey, months]) => {
            Object.entries(months || {}).forEach(([monthKey, items]) => {
              Object.values(items || {}).forEach((payment) => {
                companyMpesa.push({
                  ...payment,
                  companyName,
                  year: parseInt(yearKey, 10),
                  month: monthKey,
                  // normalize fields similar to ViewClientModal
                  date: payment.date || payment.TransactionDate || payment.paymentDate,
                  amount: parseFloat(payment.amount || payment.Amount || 0),
                  paymentMethod: "M-Pesa",
                  transactionRef: payment.transactionId || payment.TransactionID || payment.TransactionRef,
                });
              });
            });
          });
        }
        mpesaByCompany[companyName] = companyMpesa;

        // Combine all mpesa payments and sort by date
        const combined = Object.values(mpesaByCompany).flat();
        combined.sort((a, b) => {
          const dateA = new Date(a.date || a.TransactionDate || 0);
          const dateB = new Date(b.date || b.TransactionDate || 0);
          return dateB - dateA;
        });
        setMpesaPayments(combined);
      });

      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((u) => u && u());
    };
  }, [clientData, connectedCompanies, getClientUsername]);

  // Filter payments by company, month, year, and search term (app payments only)
  const filteredPayments = payments.filter((payment) => {
    const matchesCompany = companyFilter === "all" || payment.companyName === companyFilter;
    const matchesMonth = selectedMonth === "all" || payment.month === selectedMonth;
    const matchesYear = selectedYear === "all" || payment.year === selectedYear;
    const query = searchTerm.trim().toLowerCase();

    const matchesSearch =
      query.length === 0 ||
      [
        payment.companyName,
        payment.month,
        payment.year,
        payment.paymentMethod || payment.Mode,
        payment.transactionRef || payment.TransactionRef,
        String(payment.amount || payment.Amount || ""),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query));

    return matchesCompany && matchesMonth && matchesYear && matchesSearch;
  });

  // Also filter mpesaPayments by same criteria
  const filteredMpesaPayments = mpesaPayments.filter((payment) => {
    const matchesCompany = companyFilter === "all" || payment.companyName === companyFilter;
    const matchesMonth = selectedMonth === "all" || payment.month === selectedMonth;
    const matchesYear = selectedYear === "all" || payment.year === selectedYear;
    const query = searchTerm.trim().toLowerCase();

    const matchesSearch =
      query.length === 0 ||
      [
        payment.companyName,
        payment.month,
        payment.year,
        payment.paymentMethod,
        payment.transactionRef,
        String(payment.amount || payment.Amount || ""),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query));

    return matchesCompany && matchesMonth && matchesYear && matchesSearch;
  });

  // Prepare recent payments for the side-by-side table: include app payments + mpesa payments
  const recentPaymentsForDisplay = [...filteredPayments, ...filteredMpesaPayments]
    .sort((a, b) => {
      const dateA = new Date(getPaymentDate(a) || a.date || a.TransactionDate || 0);
      const dateB = new Date(getPaymentDate(b) || b.date || b.TransactionDate || 0);
      return dateB - dateA;
    })
    .slice(0, 8);

  // Filter purchases for display similarly (they are already sorted when set)
  const recentPurchasesForDisplay = recentPurchases.filter((p) =>
    companyFilter === "all" ? true : p.companyName === companyFilter
  );

  // Combined and sorted list used for the full payments list (paginated)
  const combinedFilteredPayments = [...filteredPayments, ...filteredMpesaPayments].sort((a, b) => {
    const dateA = new Date(getPaymentDate(a) || a.date || a.TransactionDate || 0);
    const dateB = new Date(getPaymentDate(b) || b.date || b.TransactionDate || 0);
    return dateB - dateA;
  });

  const totalRecords = combinedFilteredPayments.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
  const paginatedPayments = combinedFilteredPayments.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Ensure currentPage stays valid when totalPages changes
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Calculate summary stats (counts/amounts based on combined app + mpesa payments)
  const combinedAllPayments = [...payments, ...mpesaPayments];
  const stats = {
    total: combinedAllPayments.length,
    totalAmount: combinedAllPayments.reduce((sum, p) => sum + Number(p.amount || p.Amount || 0), 0),
    thisMonth: combinedAllPayments.filter((p) => {
      const paymentDate = new Date(getPaymentDate(p) || p.date || 0);
      const now = new Date();
      return (
        paymentDate.getMonth() === now.getMonth() &&
        paymentDate.getFullYear() === now.getFullYear()
      );
    }).length,
    thisMonthAmount: combinedAllPayments
      .filter((p) => {
        const paymentDate = new Date(getPaymentDate(p) || p.date || 0);
        const now = new Date();
        return (
          paymentDate.getMonth() === now.getMonth() &&
          paymentDate.getFullYear() === now.getFullYear()
        );
      })
      .reduce((sum, p) => sum + Number(p.amount || p.Amount || 0), 0),
  };

  // Generate list of years for dropdown
  const years = availableYears.length
    ? availableYears
    : [new Date().getFullYear()];

  const handleMakePayment = (company) => {
    setSelectedCompany(company);
    setShowPaymentModal(true);
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading payments...</div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-yellow-700 flex items-center gap-2">
                <DollarSign className="w-8 h-8" />
                Payment History
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                View all your payments across companies
              </p>
            </div>

            {/* Company selector (spinner) */}
            {connectedCompanies.length > 0 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Company:</label>
                <select
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                >
                  <option value="all">All Companies</option>
                  {connectedCompanies.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Payments"
            value={stats.total}
            color="bg-blue-100 text-blue-700"
          />
          <StatCard
            label="Total Amount"
            value={`KES ${stats.totalAmount.toLocaleString()}`}
            color="bg-green-100 text-green-700"
          />
          <StatCard
            label="This Month"
            value={stats.thisMonth}
            color="bg-yellow-100 text-yellow-700"
          />
          <StatCard
            label="This Month Amount"
            value={`KES ${stats.thisMonthAmount.toLocaleString()}`}
            color="bg-purple-100 text-purple-700"
          />
        </div>

        {/* Account Balances by Company (moved above tables) */}
        {connectedCompanies.length > 0 && (
          <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl p-6 shadow">
            <h2 className="text-xl font-bold text-yellow-800 mb-4 flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Account Balances by Company
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              Use the STK push button for instant payment. Your account number for each
              company is shown on the card.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {connectedCompanies.map((companyName) => {
                const balanceInfo = companyBalances[companyName] || {};
                const paybill = companyPaybills[companyName];
                const balance = balanceInfo.balance || 0;
                return (
                  <div
                    key={companyName}
                    className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800 mb-1">{companyName}</h3>
                        {paybill && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded px-2 py-1 mt-2">
                            <p className="text-xs text-gray-600">Account Number:</p>
                            <p className="font-mono font-bold text-yellow-800 text-sm">{paybill}</p>
                          </div>
                        )}
                      </div>
                      <Wallet className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Current Balance</p>
                      <p className={`text-2xl font-bold ${balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        KES {Math.abs(balance).toLocaleString()}
                      </p>
                      {balance < 0 && (
                        <p className="text-xs text-red-600 mt-1">Outstanding Payment</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleMakePayment(companyName)}
                      className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition flex items-center justify-center gap-2"
                    >
                      <Phone className="w-4 h-4" />
                      STK Push Payment
                    </button>
                    <button
                      onClick={() => openChatModal(companyName)}
                      className="mt-2 w-full px-4 py-2 border border-yellow-200 text-yellow-800 rounded-lg hover:bg-yellow-50 transition text-sm"
                    >
                      Message {companyName}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Side-by-side Recent Purchases & Payments */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Purchases Table */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800">Recent Purchases</h4>
              <p className="text-xs text-gray-500">
                Showing {recentPurchasesForDisplay.length} most recent
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Quantity (L)</th>
                    <th className="text-left p-2">Amount</th>
                    <th className="text-left p-2">Mode</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPurchasesForDisplay.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-4 text-center text-gray-500">
                        No purchases found
                      </td>
                    </tr>
                  ) : (
                    recentPurchasesForDisplay.map((purchase, idx) => {
                      const date = purchase.purchaseDate || purchase.date || purchase.PurchaseDate;
                      const qty = purchase.quantity || purchase.Quantity || "-";
                      const amount = Number(purchase.totalAmount || purchase.TotalAmount || purchase.amount || 0);
                      const mode = purchase.paymentMode || purchase.Mode || "-";
                      return (
                        <tr key={`${purchase.companyName}-${idx}`} className="border-t hover:bg-gray-50">
                          <td className="p-2">{date ? new Date(date).toLocaleDateString() : "—"}</td>
                          <td className="p-2">{qty}</td>
                          <td className="p-2">KES {amount.toLocaleString()}</td>
                          <td className="p-2">{mode}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payments Table (includes app payments + M-Pesa CustomerRecords payments) */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-800">Recent Payments</h4>
              <p className="text-xs text-gray-500">
                Showing {recentPaymentsForDisplay.length} most recent
              </p>
            </div>

            <div className="p-3 border-b">
              {/* NOTE: search input now also searches M-Pesa records (CustomerRecords) */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-700">Search M-Pesa / Payments:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by company, month, year, reference or amount"
                  className="ml-2 flex-1 px-2 py-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Amount</th>
                    <th className="text-left p-2">Method</th>
                    <th className="text-left p-2">Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {recentPaymentsForDisplay.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="p-4 text-center text-gray-500">
                        No payments found
                      </td>
                    </tr>
                  ) : (
                    recentPaymentsForDisplay.map((payment, idx) => {
                      const date = getPaymentDate(payment) || payment.date;
                      const amount = Number(payment.amount || payment.Amount || 0);
                      const method = payment.paymentMethod || payment.Mode || "-";
                      const refField = payment.transactionRef || payment.TransactionRef || payment.ReceiptNo || "-";
                      return (
                        <tr key={`${payment.companyName}-${payment.id || idx}`} className="border-t hover:bg-gray-50">
                          <td className="p-2">{date ? new Date(date).toLocaleDateString() : "—"}</td>
                          <td className="p-2">KES {amount.toLocaleString()}</td>
                          <td className="p-2">{method}</td>
                          <td className="p-2 text-xs text-gray-600">{refField}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Filters (kept below tables for additional filtering) */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-600" />
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedYear(value === "all" ? "all" : Number(value));
                }}
                className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="all">All Years</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Month:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="all">All Months</option>
                {MONTHS.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[220px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                (Tip) Use the search above to find M-Pesa references or amounts
              </label>
            </div>
          </div>
        </div>

        {/* No connected companies */}
        {connectedCompanies.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <DollarSign className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              No Companies Connected
            </h3>
            <p className="text-yellow-700 mb-4">
              Connect to companies to view payment history
            </p>
            <a
              href="/client/settings"
              className="inline-block px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              Go to Settings
            </a>
          </div>
        ) : filteredPayments.length === 0 && filteredMpesaPayments.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              No Payments Found
            </h3>
            <p className="text-gray-500">
              {selectedMonth === "all"
                ? selectedYear === "all"
                  ? "No payments available yet."
                  : `No payments found for ${selectedYear}`
                : selectedYear === "all"
                ? `No payments found for ${selectedMonth} (all years)`
                : `No payments found for ${selectedMonth} ${selectedYear}`}
            </p>
          </div>
        ) : (
          /* Paginated Payments List (rest of payments if user wants full list) */
          <div className="space-y-3">
            <div className="bg-white border rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-800 mb-3">All Payments</h4>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Company</th>
                      <th className="text-left p-2">Amount</th>
                      <th className="text-left p-2">Method</th>
                      <th className="text-left p-2">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPayments.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-4 text-center text-gray-500">No records on this page</td>
                      </tr>
                    ) : (
                      paginatedPayments.map((payment, idx) => {
                        const date = getPaymentDate(payment) || payment.date;
                        const amount = Number(payment.amount || payment.Amount || payment.amount || 0);
                        const method = payment.paymentMethod || payment.Mode || "-";
                        const refField = payment.transactionRef || payment.TransactionRef || payment.ReceiptNo || "-";
                        return (
                          <tr key={payment.id || `${refField}-${idx}`} className="border-t hover:bg-gray-50">
                            <td className="p-2">{date ? new Date(date).toLocaleString() : "—"}</td>
                            <td className="p-2">{payment.companyName}</td>
                            <td className="p-2">KES {amount.toLocaleString()}</td>
                            <td className="p-2">{method}</td>
                            <td className="p-2 text-xs text-gray-600">{refField}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1} - {Math.min(currentPage * PAGE_SIZE, totalRecords)} of {totalRecords} payments
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                  >
                    Prev
                  </button>

                  {/* Simple page buttons - show up to 7 pages with basic collapsing */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPage={(p) => setCurrentPage(p)}
                  />

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded disabled:opacity-50 text-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messaging */}
        <div className="mt-10 space-y-3">
          <h3 className="text-xl font-semibold text-gray-800">Message your company</h3>
          <p className="text-sm text-gray-600">
            Chat just like WhatsApp and see when the company reads your message.
            Messages are AI monitored for content violations and accounts may be suspended for abuse.
          </p>
          {connectedCompanies.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {connectedCompanies.map((companyName) => (
                <button
                  key={companyName}
                  onClick={() => openChatModal(companyName)}
                  className="px-4 py-2 bg-white border border-yellow-200 rounded-lg text-sm text-yellow-800 hover:bg-yellow-50"
                >
                  Open chat with {companyName}
                </button>
              ))}
            </div>
          ) : (
            <div className="border border-amber-200 bg-amber-50 text-amber-800 rounded-xl p-4 text-sm">
              Connect to a company to start messaging.
            </div>
          )}
        </div>

        <ChatModal
          open={isChatModalOpen && !!chatModalCompany && !!chatModalParticipant}
          onClose={() => setIsChatModalOpen(false)}
          title={`Chat with ${chatModalCompany || "company"}`}
          messagingProps={{
            companyName: chatModalCompany,
            participantId: chatModalParticipant,
            actorType: "participant",
            title: `Client ↔ ${chatModalCompany || "company"} chat`,
            onConversationDeleted: () => {
              setChatModalCompany(null);
              setChatModalParticipant(null);
            },
          }}
        />

        {/* M-Pesa Payment Modal */}
        {showPaymentModal && (
          <MpesaPaymentModal
            open={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedCompany(null);
            }}
            companyName={selectedCompany}
            paybill={companyPaybills[selectedCompany]}
          />
        )}
      </div>
    </ClientLayout>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className={`rounded-xl p-4 shadow ${color}`}>
    <p className="text-sm font-medium">{label}</p>
    <p className="text-xl font-bold mt-1">{value}</p>
  </div>
);

// Small Pagination component - shows up to 7 page buttons with basic collapsing
const Pagination = ({ currentPage, totalPages, onPage }) => {
  if (totalPages <= 7) {
    return (
      <>
        {Array.from({ length: totalPages }).map((_, i) => {
          const page = i + 1;
          const isCurrent = page === currentPage;
          return (
            <button
              key={page}
              onClick={() => onPage(page)}
              className={`px-3 py-1 border rounded text-sm ${isCurrent ? "bg-yellow-600 text-white" : ""}`}
            >
              {page}
            </button>
          );
        })}
      </>
    );
  }

  // totalPages > 7: show first, prev-ellipsis, centered range, next-ellipsis, last
  const pages = [];

  pages.push(1);

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 4;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 3;
    end = totalPages - 1;
  }

  if (start > 2) {
    pages.push("left-ellipsis");
  }

  for (let p = start; p <= end; p++) pages.push(p);

  if (end < totalPages - 1) {
    pages.push("right-ellipsis");
  }

  pages.push(totalPages);

  return (
    <>
      {pages.map((p, idx) => {
        if (p === "left-ellipsis" || p === "right-ellipsis") {
          return (
            <span key={`el-${idx}`} className="px-2 text-sm text-gray-500">
              ...
            </span>
          );
        }
        const isCurrent = p === currentPage;
        return (
          <button
            key={p}
            onClick={() => onPage(p)}
            className={`px-3 py-1 border rounded text-sm ${isCurrent ? "bg-yellow-600 text-white" : ""}`}
          >
            {p}
          </button>
        );
      })}
    </>
  );
};

const PaymentCard = ({ payment }) => {
  const amount = Number(payment.amount || payment.Amount || 0);
  const paymentDate = getPaymentDate(payment) || payment.date;
  const paymentMethod = payment.paymentMethod || payment.Mode || "Unknown";

  return (
    <div className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-5 h-5 text-yellow-600" />
            <span className="font-semibold text-gray-800">
              Payment #{payment.id || payment.ReceiptNo || (payment.transactionRef || "").slice(-6)}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">
            Company: <span className="font-medium">{payment.companyName}</span>
          </p>
          <p className="text-sm text-gray-600 mb-1">
            Method: <span className="font-medium">{paymentMethod}</span>
          </p>
          {payment.details || payment.Details ? (
            <p className="text-sm text-gray-500 mt-2">
              {payment.details || payment.Details}
            </p>
          ) : null}
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-green-700">
            KES {amount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {paymentDate ? new Date(paymentDate).toLocaleString() : "—"}
          </p>
          {payment.status && (
            <span
              className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${
                payment.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : payment.status === "pending"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {payment.status}
            </span>
          )}
        </div>
      </div>

      {/* Transaction Reference */}
      {payment.transactionRef || payment.TransactionRef || payment.ReceiptNo ? (
        <div className="mt-3 pt-3 border-t">
          <p className="text-xs text-gray-500">
            Ref: {payment.transactionRef || payment.TransactionRef || payment.ReceiptNo}
          </p>
        </div>
      ) : null}
    </div>
  );
};

// M-Pesa Payment Modal Component
const MpesaPaymentModal = ({ open, onClose, companyName, paybill }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [processing, setProcessing] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber || !amount) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!paybill) {
      toast.error("Paybill number not found for this company");
      return;
    }

    setProcessing(true);

    try {
      // Get Firebase ID token for authentication
      const user = auth.currentUser;
      if (!user) {
        toast.error("Please login to continue");
        setProcessing(false);
        return;
      }

      const idToken = await user.getIdToken();

      // Call Django API endpoint
      const response = await fetch(`${API_BASE_URL}/api/mpesa/stk-push/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          amount: Math.round(parseFloat(amount)), // M-Pesa only accepts whole numbers
          account_reference: paybill,
          transaction_desc: `Payment to ${companyName}`,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Payment request sent! Please check your phone to complete the payment.");
        onClose();
      } else {
        toast.error(data.error || "Failed to initiate payment");
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Phone className="w-6 h-6 text-yellow-600" />
            M-Pesa Payment
          </h2>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-yellow-800 mb-1">Paying to:</p>
          <p className="text-lg font-bold text-yellow-900">{companyName}</p>
          <div className="mt-3 space-y-2">
            {paybill ? (
              <div className="bg-white rounded px-3 py-2 border border-yellow-300">
                <p className="text-xs text-gray-600">Your Paybill / Account Number:</p>
                <p className="text-lg font-mono font-bold text-yellow-800">{paybill}</p>
                <p className="text-xs text-gray-500 mt-1">
                  This identifies you for payments to {companyName}.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded px-3 py-2 border border-yellow-300">
                <p className="text-xs text-gray-600">Paybill not available yet.</p>
                <p className="text-sm text-gray-700">
                  Please try again in a moment or contact support.
                </p>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="254XXXXXXXXX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={processing}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your M-Pesa registered phone number
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (KES)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="100"
              min="1"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              disabled={processing}
              required
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> You will receive an M-Pesa prompt on your phone.
              Enter your PIN to complete the payment. The payment will be credited to
              your account with {companyName}.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4" />
                  Send Payment Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientPayments;
